/**
 * FieldPatchQueue.tsx — Phase 6
 *
 * Listens for `studio:fieldPatchReady` CustomEvents dispatched by sendToAI
 * when the AI response contains <FIELD_UPDATE> blocks. Shows a confirmation
 * panel; on accept writes FieldEnvelope values into the Yjs doc via the
 * apply callback provided by the dispatcher (closure over conn.techPack).
 */

import React, { useState, useEffect } from "react";
import type { FieldPatch } from "./adapters/chat_patch_to_form_adapter.js";
import { highlightPatches } from "./adapters/chat_patch_to_form_adapter.js";

interface PendingQueue {
  patches: FieldPatch[];
  apply: (accepted: FieldPatch[]) => void;
}

export function FieldPatchQueue() {
  const [queue, setQueue] = useState<PendingQueue | null>(null);

  useEffect(() => {
    function onReady(e: Event) {
      const detail = (e as CustomEvent<PendingQueue>).detail;
      if (detail?.patches?.length) setQueue(detail);
    }
    window.addEventListener("studio:fieldPatchReady", onReady);
    return () => window.removeEventListener("studio:fieldPatchReady", onReady);
  }, []);

  if (!queue) return null;

  function applyAll() {
    queue!.apply(queue!.patches);
    highlightPatches(queue!.patches);
    setQueue(null);
  }

  function applySingle(patch: FieldPatch) {
    queue!.apply([patch]);
    highlightPatches([patch]);
    setQueue(prev => {
      if (!prev) return null;
      const remaining = prev.patches.filter(p => p.fieldPath !== patch.fieldPath);
      return remaining.length ? { ...prev, patches: remaining } : null;
    });
  }

  function dismiss() { setQueue(null); }

  return (
    <div
      role="dialog"
      aria-label="AI field suggestions"
      style={{
        position: "fixed", bottom: 80, right: 16, zIndex: 9999,
        background: "#1e1e2e", border: "1px solid #3b3b5c",
        borderRadius: 8, padding: 12, width: 320,
        boxShadow: "0 4px 24px rgba(0,0,0,0.5)", color: "#e0e0f0",
        fontFamily: "system-ui, sans-serif", fontSize: 13,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <strong style={{ fontSize: 14 }}>
          AI suggested {queue.patches.length} field update{queue.patches.length !== 1 ? "s" : ""}
        </strong>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: 16, lineHeight: 1 }}
        >✕</button>
      </div>
      <div style={{ maxHeight: 240, overflowY: "auto", marginBottom: 8 }}>
        {queue.patches.map(p => (
          <div
            key={p.fieldPath}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid #2a2a3e" }}
          >
            <div style={{ flex: 1, overflow: "hidden" }}>
              <div style={{ color: "#a0a0c0", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
                {p.fieldPath}
              </div>
              <div style={{ color: "#e0e0f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {p.suggestedValue}
              </div>
            </div>
            <button
              onClick={() => applySingle(p)}
              style={{ marginLeft: 8, padding: "2px 8px", background: "#3b5bdb", border: "none", borderRadius: 4, color: "#fff", cursor: "pointer", fontSize: 12 }}
            >
              Apply
            </button>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={applyAll}
          style={{ flex: 1, padding: "6px 0", background: "#3b5bdb", border: "none", borderRadius: 4, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
        >
          Apply All
        </button>
        <button
          onClick={dismiss}
          style={{ flex: 1, padding: "6px 0", background: "#2a2a3e", border: "1px solid #3b3b5c", borderRadius: 4, color: "#a0a0c0", cursor: "pointer", fontSize: 13 }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
